import {AfterViewInit, Component, ElementRef, HostListener, inject, signal, viewChild} from '@angular/core';
import {ArtworkService, UserService} from "@api-services";
import {rxResource} from "@angular/core/rxjs-interop";
import {ButtonComponent, NewsletterSubscribeComponent, EditableTextDirective} from "@components";
import {map, tap} from "rxjs/operators";
import {TranslocoDirective} from "@jsverse/transloco";
import {RouterLink} from "@angular/router";
import {CurrencyPipe, NgTemplateOutlet} from "@angular/common";
import {WINDOW} from "../../../injector";
import {ArtworkArtistPipe, DisplayNamePipe} from "../../../pipes/display-name.pipe";

@Component({
  selector: 'wun-home',
  standalone: true,
  imports: [
    CurrencyPipe,
    ArtworkArtistPipe,
    DisplayNamePipe,
    TranslocoDirective,
    ButtonComponent,
    RouterLink,
    NgTemplateOutlet,
    NewsletterSubscribeComponent,
    EditableTextDirective
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements AfterViewInit {

  // Fallback texts for editable translations
  readonly fallbacks = {
    hero: {
      title: 'Discovering the world of contemporary art with Wundart',
      subtitle: 'A curated online platform of contemporary art for artists, galleries and collectors'
    },
    joinWundart: 'Join Wundart',
    signup: {
      artist: {
        title: 'Artist',
        description: 'Sell your work',
        joinNow: 'Join Now'
      },
      gallery: {
        title: 'Gallery',
        description: 'Expand your reach',
        joinNow: 'Join Now'
      },
      collector: {
        title: 'Collector',
        description: 'Discover and collect',
        joinNow: 'Join Now'
      }
    },
    newArtworks: {
      title: 'New Artworks',
      discovery: 'Discover'
    },
    newArtists: {
      title: 'New Artists',
      discovery: 'Discover'
    },
    forGalleries: {
      title: 'For Galleries',
      subtitle: 'Wundart services for Galleries',
      description: 'Wundart gives your gallery and your artists the stage they deserve — supported by expert guidance, global exposure, and a vibrant community.',
      readMore: 'Read more'
    },
    forArtists: {
      title: 'For Artists',
      subtitle: 'Wundart services for Artists',
      description: 'Wundart gives your work the stage it deserves — supported by expert guidance, global exposure, and a vibrant community.',
      readMore: 'Read more'
    }
  };

  public readonly artistsScroll = viewChild<ElementRef<HTMLDivElement>>('artistsScrollElement');
  public readonly artworksScroll = viewChild<ElementRef<HTMLDivElement>>('artworksScrollElement');

  private readonly artworkService = inject(ArtworkService);
  private readonly userService = inject(UserService);

  private readonly window = inject(WINDOW)

  @HostListener('window:resize', ['$event'])
  onResize($event: any) {
    this.updateArrows();
  }

  artworks = rxResource({
    stream: p => this.artworkService.artworksFindAndPaginate({
      pageSize: 25,
      includes: JSON.stringify(['mainAttachment']),
      sorting: JSON.stringify({score: 'DESC'}),
      filters: JSON.stringify({
        status: 'EVALUATION_ACCEPTED'
      })
    }).pipe(
      tap(d => {
        setTimeout(() => {
          this.updateArrows();
        }, 100)
      })
    )
  })
  artists = rxResource({
    stream: p => this.userService.usersGetArtists({
      pageSize: 25,
      includes: JSON.stringify(['cover', 'covers']),
      sorting: JSON.stringify({score: 'DESC'}),
      filters: JSON.stringify({
        status: 'ACTIVE'
      })
    }).pipe(
      map(d => {
        return {
          ...d,
          data: d.data.map(usr => {
            const media = usr.covers.find(f => f.ref === usr.cover?.ref && f.fileScaling === 'GRID');
            return {
              ...usr,
              cover: media
            }
          })
        }
      }),
      tap(d => {
        setTimeout(() => {
          this.updateArrows();
        }, 100)
      })
    )
  })
  public showLeftArrow = signal(false);
  public showRightArrow  = signal(false);

  public showLeftArrowArtworks = signal(false);
  public showRightArrowArtworks  = signal(false);

  private updateArrows() {
    if(!this.artistsScroll()) return;
    const el = this.artistsScroll()!.nativeElement;
    this.showLeftArrow.set(el.scrollLeft > 0);
    this.showRightArrow.set(el.scrollLeft + el.clientWidth < el.scrollWidth);

    const el2 = this.artworksScroll()!.nativeElement;

    this.showLeftArrowArtworks.set(el2.scrollLeft > 0);
    this.showRightArrowArtworks.set(el2.scrollLeft + el2.clientWidth < el2.scrollWidth);
  }

  ngAfterViewInit(): void {
    this.updateArrows();
  }

  onScroll() {
    this.updateArrows();
  }

  scrollLeft(el: ElementRef<HTMLDivElement>) {
    const div = el.nativeElement;
    const next = this.findPrevItemOffset(div);
    div.scrollTo({ left: next, behavior: 'smooth' });
  }

  scrollRight(el: ElementRef<HTMLDivElement>) {
    const div = el.nativeElement;
    const next = this.findNextItemOffset(div);
    div.scrollTo({ left: next, behavior: 'smooth' });
  }

  private findNextItemOffset(el: HTMLDivElement): number {
    const children = Array.from(el.children) as HTMLElement[];
    const currentRight = el.scrollLeft + el.clientWidth;

    for (const child of children) {
      const childRight = child.offsetLeft + child.offsetWidth;
      if (childRight > currentRight + 5) {
        return child.offsetLeft;
      }
    }

    return el.scrollWidth - el.clientWidth;
  }

  private findPrevItemOffset(el: HTMLDivElement): number {
    const children = Array.from(el.children) as HTMLElement[];
    const currentLeft = el.scrollLeft;

    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i];
      if (child.offsetLeft < currentLeft - 5) {
        return child.offsetLeft;
      }
    }

    return 0;
  }
}
